// frontend/src/components/DocumentViewer/PDFView.tsx

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import ViewerControls from "./ViewerControls";
import FallbackPreview from "./FallbackPreview";
import LoadingSpinner from "../ui/LoadingSpinner";

interface PDFViewProps {
  url: string;
  onLoadError?: (error: Error) => void;
  onLoadComplete?: () => void;
  onFullscreen?: () => void;
  showControls?: boolean;
  isFullscreenMode?: boolean;
  fitToWidth?: boolean;
  canDownload?: boolean;
}

export interface PDFViewRef {
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleResetZoom: () => void;
  handleDownload: () => void;
}

const PDFView = forwardRef<PDFViewRef, PDFViewProps>(({
  url,
  onLoadError,
  onLoadComplete,
  onFullscreen,
  showControls = true,
  isFullscreenMode = false,
  fitToWidth = false,
  canDownload = true,
}, ref) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [zoomLevel, setZoomLevel] = useState(100);
  const containerRef = useRef<HTMLDivElement>(null);

  // Expose functions via ref
  useImperativeHandle(ref, () => ({
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    handleDownload,
  }), []);

  useEffect(() => {
    let cancelled = false;
    let currentPdfUrl = "";

    const loadPdf = async () => {
      try {
        setLoading(true);
        const response = await fetch(url, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Could not load PDF");
        }

        const blob = await response.blob();
        const pdfObjectUrl = URL.createObjectURL(blob);
        
        if (!cancelled) {
          currentPdfUrl = pdfObjectUrl;
          setPdfUrl(pdfObjectUrl);
          setError(null);
          // PDF loading simulation - real iframe load is hard to detect
          setTimeout(() => {
            onLoadComplete?.();
          }, 800);
        } else {
          URL.revokeObjectURL(pdfObjectUrl);
        }
      } catch (err) {
        if (!cancelled) {
          const error = err instanceof Error ? err : new Error("Unknown error");
          setError(error);
          onLoadError?.(error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (currentPdfUrl) {
        URL.revokeObjectURL(currentPdfUrl);
      }
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl("");
      }
    };
  }, [url, onLoadError]);

  const handleFullscreen = () => {
    if (onFullscreen) {
      onFullscreen();
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col w-full h-full">
        <FallbackPreview type="pdf" message="PDF non disponibile" />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex-1 bg-white relative">
        {loading ? (
          <LoadingSpinner message="Caricamento PDF..." />
        ) : pdfUrl ? (
          <iframe
            src={`${pdfUrl}#toolbar=0&view=FitH&zoom=${zoomLevel}`}
            className="w-full h-full border-0"
            title="PDF Viewer"
          />
        ) : null}
      </div>

      {showControls && (
        <div className="h-12 bg-gray-50 flex items-center justify-center">
          <ViewerControls 
            onFullscreen={handleFullscreen}
            onZoomIn={isFullscreenMode ? handleZoomIn : undefined}
            onZoomOut={isFullscreenMode ? handleZoomOut : undefined}
            onResetZoom={isFullscreenMode ? handleResetZoom : undefined}
            onDownload={isFullscreenMode ? handleDownload : undefined}
            isFullscreenMode={isFullscreenMode}
            canDownload={canDownload}
          />
        </div>
      )}
    </div>
  );
});

PDFView.displayName = 'PDFView';

export default PDFView;
